import React from 'react'
import toast from 'react-hot-toast'
import {
  getStoredUser,
  setStoredUser,
  getStoredProgramId,
  setStoredProgramId,
  getStoredClassId,
  setStoredClassId,
  clearTokens,
  getAccessToken,
} from '../api/client'
import * as authApi from '../api/auth.api'
import { listPrograms, listClasses } from '../api/programs.api'
import { getSettings } from '../api/settings.api'
import { readStored, writeStored } from '../lib/safeStorage'
import { halamanAwal } from '../lib/halamanAwal'
import { brandVars, BRAND_VARS } from '../lib/brandTheme'
import { useTheme } from '../lib/theme'

export const AppContext = React.createContext(null)

export function useApp() {
  return React.useContext(AppContext)
}

export function AppProvider({ children }) {
  // Bahasa bawaan Indonesia; pilihan pengguna diingat per peramban. Akses
  // localStorage bisa melempar galat (mode privat, data situs diblokir), jadi
  // semuanya dibungkus try/catch dan aplikasi tetap jalan tanpanya.
  const [lang, setLangState] = React.useState(() => {
    try {
      const saved = localStorage.getItem('gladhy_lang')
      return saved === 'en' || saved === 'id' ? saved : 'id'
    } catch {
      return 'id'
    }
  })
  const setLang = React.useCallback((next) => {
    setLangState(next)
    try { localStorage.setItem('gladhy_lang', next) } catch { /* abaikan */ }
  }, [])
  const [user, setUser] = React.useState(() => getStoredUser())
  const [programs, setPrograms] = React.useState([])
  const [currentProgramId, _setCurrentProgramId] = React.useState(() => getStoredProgramId())
  // Kelas yang sedang dibuka di dalam program itu — satu kali penyelenggaraan.
  const [classes, setClasses] = React.useState([])
  const [currentClassId, _setCurrentClassId] = React.useState(() => getStoredClassId())
  const [page, setPage] = React.useState(() => halamanAwal(getAccessToken(), getStoredUser()))
  const [pageState, setPageState] = React.useState({})
  // Mobile nav drawer (off-canvas sidebar). Desktop ignores this.
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  // Sidebar desktop yang diciutkan jadi rail ikon. Pilihan ini kenyamanan
  // per-peramban, bukan data akun, jadi cukup di localStorage.
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(
    () => readStored('gladhy_sidebar_collapsed') === '1',
  )
  const toggleSidebar = React.useCallback(() => {
    setSidebarCollapsed((prev) => {
      writeStored('gladhy_sidebar_collapsed', prev ? '0' : '1')
      return !prev
    })
  }, [])
  // FR-28: white-label identity of the institution being addressed. Loaded
  // before login so the sign-in screen already shows their name and logo.
  const [branding, setBranding] = React.useState(null)

  const isAuthenticated = !!user && !!getAccessToken()

  // Map server role enum → UI role string used by Sidebar/TopNav
  const role = React.useMemo(() => {
    if (!user) return 'participant'
    if (user.role === 'PARTICIPANT') return 'participant'
    if (user.role === 'TRAINER') return 'trainer'
    // FR-28: platform staff sit above the institutions and get their own
    // navigation — the institution menus mostly do not apply to them.
    if (user.role === 'PLATFORM_OWNER') return 'platform'
    return 'admin' // SUPER_ADMIN | PROGRAM_ADMIN
  }, [user])

  const setCurrentProgramId = React.useCallback((id) => {
    _setCurrentProgramId(id)
    setStoredProgramId(id)
    // Ingat juga di server supaya pilihan ini bertahan setelah keluar dan ikut
    // ke perangkat lain — localStorage saja tidak cukup, sebab keluar dari akun
    // justru membersihkannya.
    //
    // Tidak ditunggu dan galatnya diabaikan: ini kenyamanan, bukan syarat
    // berpindah program. Hanya untuk pemilihan sungguhan — id null datang dari
    // proses keluar dan pembersihan program basi, dan keduanya tidak boleh
    // menghapus ingatan (saat keluar, tokennya pun sudah tidak ada lagi).
    if (id && getAccessToken()) {
      authApi.setCurrentProgram(id).catch(() => {})
    }
  }, [])

  const setCurrentClassId = React.useCallback((id) => {
    _setCurrentClassId(id)
    setStoredClassId(id)
  }, [])

  // Dibaca di dalam navigate() supaya tahu halaman yang SEDANG aktif tanpa
  // membuat navigate() berubah identitas tiap kali `page` berganti (ia dipakai
  // di dependency array banyak efek di App.jsx sebagai fungsi yang stabil).
  const pageRef = React.useRef(page)
  pageRef.current = page

  // Menekan pushState() saat navigate() ini sendiri dipanggil DARI dalam
  // penanganan popstate di bawah — tanpa penjaga ini, menekan tombol back
  // akan memicu navigate() → pushState() lagi, sehingga tombol back
  // berikutnya terasa seperti tidak pernah mundur (riwayatnya tumbuh terus,
  // bukan berkurang).
  const sedangPopRef = React.useRef(false)

  const navigate = React.useCallback((p, state = {}) => {
    if (p === 'pretest') state = { ...state, kind: 'pretest' }
    if (p === 'posttest') state = { ...state, kind: 'posttest' }
    setPage(p)
    setPageState(state)
    setDrawerOpen(false) // close the mobile drawer whenever we navigate
    // Scroll to top on navigation — target both legacy .main and new .content
    document.querySelector('.content')?.scrollTo(0, 0)
    document.querySelector('.main')?.scrollTo(0, 0)

    // Riwayat browser disinkronkan dengan `page` di sini — sebelumnya
    // navigate() murni state React tanpa pernah menyentuh history, jadi
    // browser hanya mencatat SATU entry untuk seluruh sesi app. Akibatnya
    // tombol/gesture back di mobile langsung menutup tab, bukan mundur ke
    // layar sebelumnya di dalam app (tidak ada apa pun untuk di-"pop").
    //
    // Navigasi ke halaman yang SAMA (mis. klik logo saat sudah di dashboard)
    // sengaja tidak mendorong entry baru — kalau tidak, tombol back jadi
    // perlu ditekan berkali-kali untuk pindah satu layar sungguhan.
    if (!sedangPopRef.current && p !== pageRef.current) {
      window.history.pushState({ oscePage: p, oscePageState: state }, '', window.location.href)
    }
  }, [])

  // Entry PALING AWAL sesi juga perlu bermuatan state — tanpa ini,
  // event.state entry pertama itu tetap null, dan popstate pertama (mundur
  // dari layar kedua ke layar pertama) tidak tahu harus kembali ke halaman
  // apa. Hanya sekali saat mount, memakai nilai page/pageState awal.
  React.useEffect(() => {
    window.history.replaceState(
      { oscePage: pageRef.current, oscePageState: pageState }, '', window.location.href
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Tombol/gesture back memicu popstate, bukan navigate() — dengarkan di
  // sini dan terjemahkan balik ke page/pageState, supaya back benar-benar
  // mundur di dalam app alih-alih browser kehabisan riwayat lalu menutup tab.
  React.useEffect(() => {
    function onPopState(e) {
      const tujuan = e.state?.oscePage
      // Entry di luar kendali kita (mis. sebelum app pernah memanggil
      // navigate/replaceState) — tidak ada yang bisa diperbuat, biarkan
      // perilaku bawaan browser yang menentukan.
      if (!tujuan) return
      sedangPopRef.current = true
      setPage(tujuan)
      setPageState(e.state.oscePageState || {})
      setDrawerOpen(false)
      sedangPopRef.current = false
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const refreshPrograms = React.useCallback(async () => {
    try {
      const list = await listPrograms()
      setPrograms(list)
      // Only validate / clean up the stored selection. Don't auto-pick a
      // default — that decision belongs to pickLandingPage so participants
      // with multiple programs land on the selector instead of being
      // silently dropped into list[0].
      if (currentProgramId && !list.some((p) => p.id === currentProgramId)) {
        setCurrentProgramId(null)
      }
      return list
    } catch (e) {
      console.error('Failed to load programs', e)
      return []
    }
  }, [currentProgramId, setCurrentProgramId])

  const refreshClasses = React.useCallback(async () => {
    if (!currentProgramId) { setClasses([]); return [] }
    try {
      const list = await listClasses(currentProgramId)
      setClasses(list)
      return list
    } catch {
      // Peserta dan sebagian trainer tidak berhak membaca daftar kelas; itu
      // bukan galat yang perlu ditampilkan, cukup berarti tidak ada pemilih.
      setClasses([])
      return []
    }
  }, [currentProgramId])

  // Daftar kelas mengikuti program yang sedang dibuka. Pilihan kelas yang tidak
  // ada di daftar baru DIBUANG — tanpa ini, berpindah program akan menyisakan
  // kelas milik program sebelumnya dan setiap layar menampilkan daftar kosong
  // tanpa sebab yang kelihatan.
  React.useEffect(() => {
    if (!isAuthenticated || !currentProgramId) {
      setClasses([])
      return
    }
    let batal = false
    refreshClasses().then((list) => {
      if (batal) return
      const masihAda = list.some((k) => k.id === getStoredClassId())
      if (!masihAda) setCurrentClassId(list.length === 1 ? list[0].id : null)
    })
    return () => { batal = true }
  }, [isAuthenticated, currentProgramId, refreshClasses, setCurrentClassId])

  // FR-2.4: where to land after authentication.
  // - SUPER_ADMIN / PROGRAM_ADMIN skip the selector — they use TopNav switcher
  // - 0 programs (participant/trainer) → selector renders an empty state
  // - 1 program → straight to dashboard with that program preselected
  // - 2+ programs → Program Selector Screen
  function pickLandingPage({ userRole, programs, preselectedProgramId }) {
    // FR-28: platform staff belong to no institution, so none of the
    // program-based landings apply — they go straight to the platform console.
    if (userRole === 'PLATFORM_OWNER') return { page: 'platform', programId: null }
    const isAdmin = userRole === 'SUPER_ADMIN' || userRole === 'PROGRAM_ADMIN'
    if (isAdmin) return { page: 'dashboard', programId: preselectedProgramId || programs[0]?.id || null }
    if (programs.length === 0) return { page: 'program-selector', programId: null }
    if (preselectedProgramId && programs.some((p) => p.id === preselectedProgramId)) {
      return { page: 'dashboard', programId: preselectedProgramId }
    }
    if (programs.length === 1) return { page: 'dashboard', programId: programs[0].id }
    return { page: 'program-selector', programId: null }
  }

  // Apakah dua saat ini jatuh pada hari yang sama menurut ZONA WAKTU PENGGUNA.
  //
  // Sengaja tidak membandingkan tanggal UTC. Server menyimpan UTC, dan bagi
  // pengguna di WIB (UTC+7) hari UTC berganti pukul 07.00 pagi — sambutan
  // "harian" akan muncul di tengah jam kerja alih-alih saat pertama membuka
  // aplikasi. Peramban tahu zona waktunya sendiri, jadi perbandingan di sini
  // benar untuk zona mana pun tanpa menanam asumsi Asia/Jakarta ke dalam kode.
  function hariYangSama(a, b) {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
  }

  // Staf (pelatih/admin) melihat AdminWelcomeScreen sekali SEHARI.
  //
  // Kartu di dalamnya bukan sekadar perkenalan: ada ringkasan pekerjaan yang
  // menunggu hari itu. Karena itu ia berguna berulang — tetapi tidak pada
  // setiap kali masuk, sebab layar antara yang muncul terus-menerus akan
  // ditekan tanpa dibaca, dan angkanya justru berhenti terbaca.
  //
  // Penandanya milik AKUN, bukan peramban. Dulu di localStorage, sehingga
  // melekat pada perangkat: begitu satu staf pernah masuk di sebuah komputer,
  // admin berikutnya di komputer yang sama tidak pernah melihatnya.
  //
  // Diputuskan di sini saat masuk (bukan di effect yang bersaing) supaya
  // navigasi pendaratan tidak langsung menimpanya.
  function staffWelcomeLanding(serverRole, terakhirDilihatISO) {
    if (serverRole === 'PARTICIPANT') return null
    // The welcome tour is about running a program; platform staff never do.
    if (serverRole === 'PLATFORM_OWNER') return null

    const terakhir = terakhirDilihatISO ? new Date(terakhirDilihatISO) : null
    if (terakhir && !Number.isNaN(terakhir.getTime()) && hariYangSama(terakhir, new Date())) {
      return null
    }
    // Tidak ditunggu: gagalnya penandaan tidak boleh menahan orang masuk.
    // Paling buruk sambutannya muncul sekali lagi di kesempatan berikutnya.
    authApi.markStaffWelcomeSeen().catch(() => {})
    return 'welcome'
  }

  // Sesudah sesi penuh didapat (login sandi, atau sandi + kode 2FA): muat
  // program lalu mendarat di tempat yang tepat.
  const finishLogin = React.useCallback(
    async (data) => {
      setUser(data.user)
      // Wajib 2FA tetapi belum mendaftar: token masih terbatas, jadi memuat
      // program pasti ditolak. Langsung ke layar pendaftaran.
      if (data.user.mfaSetupRequired) {
        navigate('mfa-setup')
        return data
      }
      const list = await refreshPrograms()
      const landing = pickLandingPage({
        userRole: data.user.role,
        programs: list,
        // Kode kelas dan QR membawa programnya sendiri; masuk dengan email+sandi
        // tidak. Tanpa ini peserta yang terdaftar di lebih dari satu program
        // dipaksa memilih ulang setiap kali, padahal pilihannya hampir selalu
        // sama dengan kemarin.
        //
        // Server yang jadi sumber utama (bertahan lintas perangkat dan setelah
        // keluar); nilai di browser hanya cadangan untuk akun lama yang belum
        // sempat mencatatnya. pickLandingPage tetap mencocokkannya dengan daftar
        // program yang benar-benar dimiliki, jadi nilai basi terabaikan sendiri.
        preselectedProgramId: data.currentProgramId || getStoredProgramId(),
      })
      if (landing.programId) setCurrentProgramId(landing.programId)
      navigate(staffWelcomeLanding(data.user.role, data.staffWelcomeSeenAt) || landing.page)
      return data
    },
    [navigate, refreshPrograms, setCurrentProgramId]
  )

  const login = React.useCallback(
    async (email, password) => {
      const data = await authApi.login(email, password)
      // Akun ber-2FA: belum ada sesi. LoginScreen meminta kode, lalu memanggil
      // completeMfaLogin.
      if (data.mfaRequired) return data
      return finishLogin(data)
    },
    [finishLogin]
  )

  const completeMfaLogin = React.useCallback(
    async (mfaToken, code) => finishLogin(await authApi.verifyLogin2fa(mfaToken, code)),
    [finishLogin]
  )

  // Pendaftaran 2FA selesai: tanda "wajib daftar" dicabut dan pengguna masuk ke
  // aplikasi seperti login biasa.
  const finishMfaSetup = React.useCallback(async () => {
    const baru = { ...(user || {}), mfaSetupRequired: false }
    setUser(baru)
    setStoredUser(baru)
    const list = await refreshPrograms()
    const landing = pickLandingPage({ userRole: baru.role, programs: list, preselectedProgramId: getStoredProgramId() })
    if (landing.programId) setCurrentProgramId(landing.programId)
    navigate(landing.page)
  }, [user, navigate, refreshPrograms, setCurrentProgramId])

  // Gabung kelas — peserta yang datanya belum pernah ada di sistem. Mendarat
  // langsung di dasbor: server sudah menandai enrolmentnya sebagai selesai
  // berkenalan, sebab orang yang sedang duduk menunggu pretest tidak butuh
  // layar perkenalan.
  const joinWithCode = React.useCallback(
    async ({ code, fullName, company, placeOfBirth, dateOfBirth, email, phone, password }) => {
      const data = await authApi.joinWithCode({ code, fullName, company, placeOfBirth, dateOfBirth, email, phone, password })
      setUser(data.user)
      const list = await refreshPrograms()
      const landing = pickLandingPage({
        userRole: data.user.role,
        programs: list,
        preselectedProgramId: data.currentProgramId || null,
      })
      if (landing.programId) setCurrentProgramId(landing.programId)
      navigate(landing.page)
      return data
    },
    [navigate, refreshPrograms, setCurrentProgramId]
  )

  const loginWithQR = React.useCallback(
    async (qrToken) => {
      // FR-1.7: include device fingerprint so the backend can flag unknown devices.
      let device = {}
      try {
        const { getDeviceFingerprint } = await import('../lib/device')
        device = await getDeviceFingerprint()
      } catch { /* crypto.subtle unavailable in legacy contexts — non-fatal */ }
      const data = await authApi.loginWithQR(qrToken, device)
      setUser(data.user)
      const list = await refreshPrograms()
      // QR carries an embedded programId; honor it if user actually has access.
      const landing = pickLandingPage({
        userRole: data.user.role,
        programs: list,
        preselectedProgramId: data.currentProgramId || null,
      })
      if (landing.programId) setCurrentProgramId(landing.programId)
      navigate(staffWelcomeLanding(data.user.role, data.staffWelcomeSeenAt) || landing.page)
      if (data.newDevice) {
        // Fired so any host component (e.g. a settings page) can show a
        // confirmation prompt. We don't block login.
        window.dispatchEvent(
          new CustomEvent('osct:new-device', { detail: { deviceId: data.deviceId } })
        )
      }
      return data
    },
    [navigate, refreshPrograms, setCurrentProgramId]
  )

  const logout = React.useCallback(async () => {
    await authApi.logout()
    setUser(null)
    setPrograms([])
    setCurrentProgramId(null)
    setStoredUser(null)
    navigate('login')
  }, [navigate, setCurrentProgramId])

  // Auto-load programs after login
  React.useEffect(() => {
    if (isAuthenticated && programs.length === 0) {
      refreshPrograms().then((list) => {
        // Peserta/trainer tanpa satu pun program yang terlihat — mis. programnya
        // dinonaktifkan selagi sesinya masih berjalan, lalu halaman dimuat ulang —
        // jatuh ke dasbor kosong ("0 kursus aktif"), bukan ke layar "Belum ada
        // program" yang menjelaskan keadaannya dan memuat jalan ke sertifikat.
        // Hanya dari dasbor: halaman lain (sertifikat, klaim) tidak diganggu.
        if (list.length === 0 && (role === 'participant' || role === 'trainer') && page === 'dashboard') {
          navigate('program-selector')
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, programs.length, refreshPrograms])

  // FR-28: load the institution's branding once on boot — no auth needed, so
  // the login screen already carries their identity. Failure is non-fatal;
  // the UI falls back to its built-in labels.
  React.useEffect(() => {
    let cancelled = false
    getSettings()
      .then((data) => { if (!cancelled) setBranding(data) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Browser tab follows the institution, so a bookmarked tab is recognisable.
  React.useEffect(() => {
    const name = branding?.institution_name
    if (name) document.title = name
  }, [branding])

  // Ikon tab: milik lembaga bila ada (semua paket). Pada white-label tanpa
  // favicon, logo lembaga dipakai daripada ikon Gladhy. Selebihnya tetap ikon
  // platform dari index.html, yang sudah terpasang sebelum branding dimuat.
  React.useEffect(() => {
    const link = document.querySelector('link[rel="icon"]')
    if (!link) return undefined
    const bawaan = { href: link.getAttribute('href'), type: link.getAttribute('type') }
    const own = branding?.favicon_url || (branding?.hide_platform_branding && branding?.logo_url) || ''
    if (!own) return undefined
    link.setAttribute('href', own)
    // Jenis dilepas supaya peramban menentukannya dari isi berkas (png, ico, jpg).
    link.removeAttribute('type')
    return () => {
      link.setAttribute('href', bawaan.href)
      if (bawaan.type) link.setAttribute('type', bawaan.type)
    }
  }, [branding])

  // Tautan konfirmasi ganti email (?confirm-email=<token>) dari kotak masuk
  // alamat baru. Server mengakhiri semua sesi akun begitu email berganti, jadi
  // sisi klien ikut keluar dan meminta masuk dengan alamat yang baru.
  const confirmEmailDipakaiRef = React.useRef(false)
  React.useEffect(() => {
    if (confirmEmailDipakaiRef.current) return
    const url = new URL(window.location.href)
    const token = url.searchParams.get('confirm-email')
    if (!token) return
    confirmEmailDipakaiRef.current = true
    // Dibuang dari alamat segera: token tidak boleh tertinggal di riwayat peramban.
    url.searchParams.delete('confirm-email')
    window.history.replaceState({}, '', url.toString())
    authApi.confirmEmailChange(token)
      .then((r) => {
        clearTokens()
        setUser(null)
        navigate('login')
        toast.success(
          lang === 'id'
            ? `Email akun diganti menjadi ${r.email}. Masuk dengan alamat yang baru.`
            : `Account email changed to ${r.email}. Sign in with the new address.`,
          { duration: 8000 }
        )
      })
      .catch((e) => toast.error(e?.response?.data?.error || e.message, { duration: 8000 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for 401 → expired tokens
  React.useEffect(() => {
    function onExpired() {
      clearTokens()
      setUser(null)
      navigate('login')
    }
    window.addEventListener('osct:auth-expired', onExpired)
    return () => window.removeEventListener('osct:auth-expired', onExpired)
  }, [navigate])

  // Server menolak rute apa pun selain pendaftaran 2FA untuk token terbatas.
  React.useEffect(() => {
    const onSetupRequired = () => {
      setUser((u) => (u ? { ...u, mfaSetupRequired: true } : u))
      navigate('mfa-setup')
    }
    window.addEventListener('osct:mfa-setup-required', onSetupRequired)
    return () => window.removeEventListener('osct:mfa-setup-required', onSetupRequired)
  }, [navigate])

  // FR-1.7: when a login arrives from an unrecognised device, ask the user
  // to confirm before we mark the device as trusted.
  React.useEffect(() => {
    async function onNewDevice(e) {
      const deviceId = e?.detail?.deviceId
      if (!deviceId) return
      toast(
        (toastInstance) => (
          <div style={{ minWidth: 260 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {lang === 'id' ? 'Perangkat baru terdeteksi' : 'New device detected'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {lang === 'id'
                ? 'Apakah ini perangkat Anda? Kami akan ingat agar tidak menanyakan lagi.'
                : 'Is this your device? We will remember it next time.'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                className="btn sm"
                onClick={async () => {
                  try {
                    await authApi.confirmDevice(deviceId)
                    toast.success(lang === 'id' ? 'Perangkat dipercaya' : 'Device trusted')
                  } catch (err) {
                    toast.error(err?.response?.data?.error || err.message)
                  } finally {
                    toast.dismiss(toastInstance.id)
                  }
                }}
                style={{ flex: 1 }}
              >
                {lang === 'id' ? 'Ya, percaya' : 'Yes, trust'}
              </button>
              <button
                className="btn ghost sm"
                onClick={() => toast.dismiss(toastInstance.id)}
                style={{ flex: 1 }}
              >
                {lang === 'id' ? 'Nanti' : 'Later'}
              </button>
            </div>
          </div>
        ),
        { duration: 15000 }
      )
    }
    window.addEventListener('osct:new-device', onNewDevice)
    return () => window.removeEventListener('osct:new-device', onNewDevice)
  }, [lang])

  const currentProgram = React.useMemo(
    () => programs.find((p) => p.id === currentProgramId) || null,
    [programs, currentProgramId]
  )

  const currentClass = React.useMemo(
    () => classes.find((k) => k.id === currentClassId) || null,
    [classes, currentClassId]
  )

  // Accent tints derived from the colour in play, rather than looked up from a
  // fixed table. The table was keyed on the OPRC tier, so a purple programme at
  // tier 1 got green tints and a green one with no tier got teal — every
  // combination in the database was mismatched.
  // Tema terang/gelap memengaruhi turunannya (lihat efek di bawah), jadi efeknya
  // ikut dijalankan ulang saat tema berganti.
  const { resolved: theme } = useTheme()

  // Theme tokens on <html>, most specific source winning:
  //
  //   active programme's colour  >  institution's primary_color  >  the
  //   Gladhy default in styles/index.css
  //
  // The institution layer used to be missing entirely: an admin could set a
  // primary colour, see it saved, and watch the app stay the design system's
  // default — the field looked configurable but reached only the sign-in page.
  React.useEffect(() => {
    const root = document.documentElement
    const raw = currentProgram?.color_theme || branding?.primary_color
    const vars = brandVars(raw, theme)
    // Variabel yang tidak dihasilkan (warna kosong/tak valid, atau tint di tema
    // gelap) dibuang supaya stylesheet yang menentukan, bukan tebakan.
    BRAND_VARS.forEach((p) => {
      if (p in vars) root.style.setProperty(p, vars[p])
      else root.style.removeProperty(p)
    })
  }, [currentProgram, branding, theme])

  const value = {
    // i18n
    lang,
    setLang,
    // auth
    user,
    role,
    isAuthenticated,
    login,
    completeMfaLogin,
    finishMfaSetup,
    loginWithQR,
    joinWithCode,
    logout,
    // programs
    programs,
    currentProgram,
    currentProgramId,
    setCurrentProgramId,
    refreshPrograms,
    // kelas — satu kali penyelenggaraan program
    classes,
    currentClass,
    currentClassId,
    setCurrentClassId,
    refreshClasses,
    // navigation
    page,
    pageState,
    navigate,
    // mobile nav drawer
    drawerOpen,
    setDrawerOpen,
    // desktop sidebar diciutkan menjadi rail ikon
    sidebarCollapsed,
    toggleSidebar,
    // FR-28 white-label
    branding,
    setBranding,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
